import React, { useMemo } from "react";

import {
    Link,
    useLocation,
    useNavigate,
    useNavigation,
} from "@remix-run/react";
import clsx from "clsx";
import { ChevronLeft } from "lucide-react";

import Logo from "~/components/logo";
import { Button } from "~/components/ui/button";
import { H1, P } from "~/components/ui/typography";
import { GlobalLoading, useLoader } from "~/context/LoaderContext";
import { useSecondarySidebar } from "~/context/SecondarySidebarContext";
import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import { useAnonymous } from "~/hooks/use-anonymous";
import { useMatchesData } from "~/hooks/use-matches-data";
import { BillingSessionWarningModal } from "~/components/billing/billing-session-warning-modal";
import { ServiceDegradationBanner } from "~/components/service-degradation-banner";
import { BaaNudgeBanner } from "~/features/baa-nudge/components/baa-nudge-banner";
import { DpaAcceptBanner } from "~/features/baa-nudge/components/dpa-accept-banner";
import { OnboardingChecklistContainer } from "~/features/onboarding-checklist/components/onboarding-checklist-container";

import DesktopLayout from "./desktop-layout";
import { MobileTabBar } from "./mobile-tab-bar";
import { UserMenu } from "./user-menu";

/**
 * AIDEV-NOTE: Overlay that fades out on initial page load to hide hydration flashes.
 * Only renders during initial load (isInitialLoad=true), not during page transitions.
 */
function InitialLoadOverlay({ isInitialLoad }: { isInitialLoad: boolean }) {
    const [isVisible, setIsVisible] = React.useState(true);

    React.useEffect(() => {
        // Once we've determined the layout (not initial load), start fading out
        if (!isInitialLoad) {
            // Small delay to ensure the layout has painted
            const fadeTimer = setTimeout(() => {
                setIsVisible(false);
            }, 50);
            return () => clearTimeout(fadeTimer);
        }
    }, [isInitialLoad]);

    // Remove from DOM after fade completes
    const [isRemoved, setIsRemoved] = React.useState(false);
    React.useEffect(() => {
        if (!isVisible) {
            const removeTimer = setTimeout(() => {
                setIsRemoved(true);
            }, 200); // Match transition duration
            return () => clearTimeout(removeTimer);
        }
    }, [isVisible]);

    if (isRemoved) return null;

    return (
        <div
            className="fixed inset-0 z-[9999] bg-white pointer-events-none"
            style={{
                opacity: isVisible ? 1 : 0,
                transition: "opacity 200ms ease-out",
            }}
            aria-hidden="true"
        />
    );
}

export const Section = ({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) => <div className={clsx("px-0 md:px-4 py-4", className)}>{children}</div>;

export const ActionSection = ({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) => (
    <div className={clsx("w-full mx-0 md:-ml-5 md:-mr-5", className)}>
        {children}
    </div>
);

interface LayoutProps {
    children?: React.ReactNode;
    hideSidebar?: boolean;
    hideBack?: boolean;
    showLogout?: boolean;
    actions?: React.ReactNode;
    searchBar?: React.ReactNode;
    backNavOverride?: string;
    /** AIDEV-NOTE: Mobile-only — label rendered beside the back chevron (e.g. the collection
     * name), indicating where back navigates to. */
    backLabel?: string;
    loading?: boolean;
    title?: React.ReactNode;
    /** Extra classes merged onto the mobile title H1 (e.g. line-height overrides) */
    titleClassName?: string;
    /** AIDEV-NOTE: Mobile-only — render the title on its own left-aligned row BELOW the
     * masthead (chevron/back row) instead of centered inside it. */
    titleBelowHeader?: boolean;
    description?: React.ReactNode;
    /* TODO: these are anti-pattern props and should be reworked */
    displayHeader?: React.ReactNode;
    offsetTop?: boolean;
    hideLogo?: boolean;
    narrow?: boolean;
    stickyHeader?: boolean;
    headerBanner?: React.ReactNode;
    /* Desktop props */
    fullWidth?: boolean;
    /**
     * Drop <main>'s horizontal padding so the PAGE owns it.
     *
     * AIDEV-NOTE: Without this, a page wanting an edge-to-edge band (a mast, a full-width
     * banner) has to cancel main's padding with negative margins that hardcode its exact
     * values — and silently break when they change. With `bleed`, a `w-full` child really is
     * edge to edge and the page applies its own padding to whatever should stay inset.
     * Opt-in: every existing page keeps main's padding untouched.
     */
    bleed?: boolean;
    /**
     * Skip the desktop header band entirely.
     *
     * AIDEV-NOTE: <header> renders even with no title/searchBar/actions, and still carries its
     * `2xl:pt-16 pb-4` — about 80px of empty space above the content on a wide screen. Pages
     * that open with their own masthead want that band gone rather than merely empty. Desktop
     * only; mobile keeps its header, which carries the back navigation.
     */
    hideHeader?: boolean;
    /** Bound mobile workspaces to the viewport so child panes can scroll independently. */
    mobileFullHeight?: boolean;
    /** Bound desktop workspaces so their panes, rather than the document, scroll. */
    desktopFullHeight?: boolean;
    headerImageUrl?: string;
    /**
     * AIDEV-NOTE: Right sidebar content - scrolls independently from main content.
     * Rendered as a sticky column on the right side of the main content area.
     * The sidebar has its own scroll area and stays visible while main content scrolls.
     * Only shown on desktop (lg+) screens.
     */
    rightSidebar?: React.ReactNode;
    /**
     * Width of the right sidebar. Can be a number (pixels) or string (e.g., "30vw"). Defaults to 400.
     */
    rightSidebarWidth?: number | string;
    /**
     * Optional className override for right sidebar container (default: bg-gray-50)
     */
    rightSidebarClassName?: string;
    /**
     * Optional session-specific menu items for mobile hamburger menu
     */
    sessionMenuItems?: React.ReactNode;
    /**
     * Optional data-testid for the layout container (for Playwright tests)
     */
    testId?: string;
}

const MobileLayout = ({
    children,
    mobileFullHeight = false,
    hideSidebar = false,
    backNavOverride,
    backLabel,
    hideBack = false,
    showLogout = false,
    narrow = false,
    actions,
    loading = false,
    offsetTop = false,
    title,
    titleClassName,
    titleBelowHeader = false,
    description,
    hideLogo = false,
    headerImageUrl,
    sessionMenuItems,
    testId,
}: LayoutProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isRoot =
        location.pathname === "/" || location.pathname === "/landing";
    const navigation = useNavigation();
    const { user } = useSupabaseSession();

    const backNavPath = useMemo(() => {
        if (backNavOverride) {
            try {
                const url = new URL(backNavOverride);
                return url.pathname + url.search;
            } catch {
                return backNavOverride;
            }
        }
        return undefined;
    }, [backNavOverride]);

    const { isAnonymous } = useAnonymous();

    return (
        <div
            data-testid={testId}
            className={clsx(
                "flex max-h-dvh min-h-dvh max-w-none flex-col items-center justify-center overflow-y-hidden bg-brand-cream-50 print:h-auto print:max-h-none print:overflow-visible print:bg-white",
                mobileFullHeight && "h-dvh",
            )}
        >
            <GlobalLoading />
            <ServiceDegradationBanner />
            <BaaNudgeBanner />
            <DpaAcceptBanner />
            {/* AIDEV-NOTE: ScrollRestoration removed - already in root.tsx */}
            <div
                id="layout-container"
                className={clsx(
                    "@container",
                    mobileFullHeight
                        ? "min-h-0 flex-1 overflow-hidden print:overflow-visible"
                        : "grow overflow-y-auto print:overflow-visible",
                    "w-full max-w-md flex flex-col items-stretch justify-start shadow-2xl shadow-brand-terra-600/20 border-brand-terra-500/30 print:shadow-none",
                    "w-screen md:h-auto md:w-auto lg:border md:rounded-none min-w-screen max-w-screen",
                )}
                style={{
                    minWidth: "100vw",
                    maxWidth: "100vw",
                }}
            >
                {headerImageUrl && (
                    <div className="mb-4 h-[300px] w-full">
                        <img
                            src={headerImageUrl}
                            alt="Header background"
                            className="size-full object-cover"
                        />
                    </div>
                )}
                <div
                    className={clsx(
                        "w-full h-full",
                        "px-4 pb-8 pt-6 md:p-8 md:mx-auto",
                        mobileFullHeight && "flex min-h-0 flex-1 flex-col",
                    )}
                    style={{
                        maxWidth: narrow ? "32rem" : "72rem",
                        paddingTop: offsetTop ? "10vh" : undefined,
                        paddingBottom:
                            mobileFullHeight && !isAnonymous && !hideSidebar
                                ? "calc(5rem + env(safe-area-inset-bottom))"
                                : undefined,
                    }}
                >
                    <div
                        className={clsx(
                            "flex justify-start w-full px-0 md:pt-8 pb-4 background-white dropshadow",
                        )}
                    >
                        <div className="flex items-center justify-start gap-4 print:hidden">
                            {!hideBack && !isRoot && (
                                <div className="items-center justify-center">
                                    <Button
                                        variant="ghostIcon"
                                        onClick={() =>
                                            backNavPath
                                                ? navigate(backNavPath)
                                                : navigate(-1)
                                        }
                                        className="text-brand-terra-500 hover:bg-brand-terra-100 bg-transparent pl-2 pr-3"
                                        analyticsId="page back"
                                    >
                                        <ChevronLeft className="size-24" />
                                        {/* AIDEV-NOTE: backLabel = where back navigates to
                                            (e.g. the collection name), centered on the chevron. */}
                                        {backLabel && (
                                            <span className="text-sm font-medium text-brand-terra-600">
                                                {backLabel}
                                            </span>
                                        )}
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-1 items-center">
                            {title && !hideLogo && !titleBelowHeader ? (
                                <H1
                                    className={clsx(
                                        "text-brand-forest-800 mx-auto mb-1 mt-0 text-center text-2xl font-bold tracking-tight",
                                        titleClassName,
                                    )}
                                >
                                    {title}
                                </H1>
                            ) : !hideLogo && !titleBelowHeader ? (
                                <div className="flex flex-col items-center gap-6">
                                    <Logo size={"md"} variant="image" />
                                </div>
                            ) : null}
                        </div>
                        <div
                            className={clsx(
                                "space-x-4 flex items-center justify-end",
                                !hideBack && "min-w-[40px]",
                            )}
                        >
                            {isAnonymous && showLogout && (
                                <Link to="/link-account">
                                    <Button
                                        variant="outline"
                                        analyticsId="Link anonymous account"
                                    >
                                        Create an account
                                    </Button>
                                </Link>
                            )}
                            {actions}
                            {!isAnonymous && showLogout && user?.email && (
                                <div className="hidden md:block">
                                    <UserMenu />
                                </div>
                            )}
                            {/* AIDEV-NOTE: Mobile hamburger menu removed - navigation now handled by bottom tab bar */}
                        </div>
                    </div>
                    {/* AIDEV-NOTE: titleBelowHeader — title on its own left-aligned row under the
                        masthead, with breathing room above the content (Michelle Glass review,
                        Jul 7 2026: chevron+collection row → title row → content card). */}
                    {titleBelowHeader && title && (
                        // AIDEV-NOTE: pl-2 matches the back Button's pl-2 so the title's left
                        // edge lines up with the chevron above (and the content card's section
                        // title below).
                        <H1
                            className={clsx(
                                "text-brand-forest-800 mt-2 mb-5 pl-2 text-left text-2xl font-bold tracking-tight",
                                titleClassName,
                            )}
                        >
                            {title}
                        </H1>
                    )}
                    <div
                        className={clsx(
                            "flex max-w-none flex-col items-center justify-center space-y-8",
                            mobileFullHeight ? "shrink-0" : "flex-1",
                        )}
                    >
                        {description && <P>{description}</P>}
                    </div>
                    <div
                        className={clsx(
                            "flex items-start justify-center gap-8 grow md:p-4",
                            mobileFullHeight && "min-h-0 flex-1",
                            // AIDEV-NOTE: Add bottom padding for mobile tab bar (80px) + extra space (16px)
                            !isAnonymous && "",
                            isAnonymous && "",
                        )}
                    >
                        {navigation.state === "loading" || loading ? (
                            <></>
                        ) : (
                            <>
                                <div
                                    className={clsx(
                                        "flex w-full flex-1 flex-col items-center justify-center space-y-4",
                                        mobileFullHeight &&
                                            "h-full min-h-0 items-stretch justify-start",
                                    )}
                                    id="layout-body"
                                >
                                    {children}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {!hideSidebar ? <OnboardingChecklistContainer narrow /> : null}
            {/* AIDEV-NOTE: Mobile tab bar - only show for authenticated users on mobile */}
            {!isAnonymous && !hideSidebar ? <MobileTabBar /> : null}
        </div>
    );
};

/* Important: to support SSR hydration, we render both mobile and desktop layouts
   on the server. Then, after the first render, we pick which to keep around (variant)
   */
export default function ResponsiveLayout(props: LayoutProps) {
    // AIDEV-NOTE: In Playwright test mode, testLayoutOverride ("mobile"|"desktop") comes from
    // the root loader via server env var + cookie. Available during SSR + hydration so we skip
    // the "unknown" dual-render phase entirely. Tests default to desktop; set __test_layout=mobile
    // cookie for mobile. Undefined in production — falls back to dual-render + useEffect detection.
    const rootData = useMatchesData<{
        testLayoutOverride?: "mobile" | "desktop";
    }>("root");
    const testLayoutOverride = rootData?.testLayoutOverride;

    // AIDEV-NOTE: Track both variant and test status in a single state to ensure atomic updates.
    // Previously had separate states which caused timing issues where both layouts were visible.
    const [layoutState, setLayoutState] = React.useState<{
        variant: "unknown" | "mobile" | "desktop";
        isTesting: boolean;
    }>({
        variant: testLayoutOverride ?? "unknown",
        isTesting: !!testLayoutOverride,
    });

    // AIDEV-NOTE: Secondary sidebar context allows parent routes (e.g., admin) to inject
    // a secondary navigation sidebar without modifying each child page's layout usage.
    const { secondarySidebar, forceCollapsed } = useSecondarySidebar();

    React.useEffect(() => {
        // In test mode, layout is already locked via server-side detection — skip client detection
        if (testLayoutOverride) return;

        // AIDEV-NOTE: Playwright fixture sets __PLAYWRIGHT_TEST so layout detection
        // does not depend on a specific localhost port.
        const isPlaywrightTest = (
            window as Window & { __PLAYWRIGHT_TEST?: boolean }
        ).__PLAYWRIGHT_TEST;
        const mediaQuery = window.matchMedia("(max-width: 1023px)");
        const isTest = isPlaywrightTest || window.playwright || window.Cypress;

        // AIDEV-NOTE: Using matchMedia change event is performant - only fires when
        // the breakpoint is crossed, not on every resize event.
        const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
            setLayoutState((prev) => ({
                ...prev,
                variant: e.matches ? "mobile" : "desktop",
            }));
        };

        // Set initial state
        setLayoutState({
            variant: mediaQuery.matches ? "mobile" : "desktop",
            isTesting: !!isTest,
        });

        // Listen for changes
        mediaQuery.addEventListener("change", handleChange);

        return () => {
            mediaQuery.removeEventListener("change", handleChange);
        };
    }, [testLayoutOverride]);

    const { variant, isTesting } = layoutState;

    // Merge secondary sidebar props from context
    const desktopProps = {
        ...props,
        secondarySidebar,
        forceCollapsed,
    };

    const isInitialLoad = variant === "unknown";

    // AIDEV-NOTE: Honor the test layout override so mobile tests exercise the real mobile shell.
    if (isTesting && variant !== "unknown") {
        return (
            <>
                <InitialLoadOverlay isInitialLoad={isInitialLoad} />
                <BillingSessionWarningModal />
                {variant === "mobile" ? (
                    <MobileLayout {...props} />
                ) : (
                    <DesktopLayout {...desktopProps} />
                )}
            </>
        );
    }

    if (variant === "mobile" || props.narrow) {
        return (
            <>
                <InitialLoadOverlay isInitialLoad={isInitialLoad} />
                <BillingSessionWarningModal />
                <MobileLayout {...props} />
            </>
        );
    }
    if (variant === "desktop") {
        return (
            <>
                <InitialLoadOverlay isInitialLoad={isInitialLoad} />
                <BillingSessionWarningModal />
                <DesktopLayout {...desktopProps} />
            </>
        );
    }

    // AIDEV-NOTE: During SSR hydration (variant === "unknown"), render both layouts.
    // CSS will show the correct one based on viewport. After hydration, the effect
    // will set the correct variant and switch to single-layout mode.
    // pointerEvents: "auto" is defensive - CSS already hides the wrong layout via display:none.
    return (
        <>
            <InitialLoadOverlay isInitialLoad={isInitialLoad} />
            <BillingSessionWarningModal />
            {/* Mobile & tablet view: show Layout, hide on lg+ */}
            <div
                className="block size-full lg:hidden"
                style={{ pointerEvents: "auto" }}
            >
                <MobileLayout {...props} />
            </div>

            {/* Desktop view: hide below lg, show DesktopLayout on lg+ */}
            <div
                className="hidden size-full lg:block"
                style={{ pointerEvents: "auto" }}
            >
                <DesktopLayout {...desktopProps} />
            </div>
        </>
    );
}
